import { AudioSessionMeta, ChatMessageItem } from "@aidkitorg/types/lib/convoai/conversation";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { useDebouncedEffect, useDebouncedCallback } from "../Hooks/Debounce";
import { ChatContext } from "./ChatContext";
import { WebRTCContext } from "./WebRTCContext";

export type ChatSessionContextType = {
  createdAt?: Date,
  enabled: boolean,
  setEnabled(enable: boolean): void,
  isLoading: boolean,
  loaded: boolean,
  termsAccepted?: string,
  setTermsAccepted(termsHash: string): void
}

export const conversationIdCookieName = "ai_coach_conversation_id";

export const ChatSessionContext = React.createContext<ChatSessionContextType>({
  enabled: false,
  isLoading: false,
  loaded: false,
  setEnabled() { console.warn('no chat session present, ignoring setEnabled()') },
  setTermsAccepted() { console.warn('no chat session present, ignoring setTermsAccepted()') },
});

export const ChatSessionProvider: React.FC<{ context: ChatSessionContextType, children: React.ReactNode }> = ({ context, children }) =>
  <ChatSessionContext.Provider value={context}>
    {children}
  </ChatSessionContext.Provider>;

/**
 * Put within any existing ChatContext, to enable persistence of the chat.
 */
export function DefaultChatSessionProvider({ children }: { children: React.ReactNode }) {
  const chat = useContext(ChatContext);
  const getOrCreateConversation = usePost('/coach/conversation/create');
  const updateConversation = usePost('/coach/conversation/update', { keepAlive: true });
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [createdAt, setCreatedAt] = useState(new Date());
  const conversation = useRef<ChatMessageItem[]>([]);
  const audioSessionMeta = useRef<AudioSessionMeta>({ session: {} });
  const webrtc = useContext(WebRTCContext);
  const [termsAccepted, setTermsAccepted] = useState<string | undefined>();
  const [cookies, setCookie] = useCookies([conversationIdCookieName]);
  const id = cookies[conversationIdCookieName];

  useAsyncEffect(async (cancel) => {
    if (enabled && chat.active && !loaded) {

      const storedConvoId = cookies[conversationIdCookieName];
      setIsLoading(true);
      const existing = await getOrCreateConversation({ previousConvoId: storedConvoId ?? undefined }, cancel);
      if (existing && !("error" in existing)) {
        if (storedConvoId !== existing.id) {
          setCookie(conversationIdCookieName, existing.id)
        }

        if (!termsAccepted && existing?.metadata?.termsAccepted) {
          setTermsAccepted(existing?.metadata?.termsAccepted);
        }

        setCreatedAt(new Date(existing.created_at));

        if (existing.items) {
          chat.restore(existing.items);
        }

        setLoaded(true);

      }
      setIsLoading(false);
    }
  }, [enabled, chat.active, loaded, chat.sendMessage, getOrCreateConversation]);

  // when there is any change to the realtime session, update the stored conversation
  useEffect(() => {
    if (enabled && loaded) {
      chat.updateConversationMetadata({
        session: webrtc.realtimeSession ?? undefined,
        errors: [
          ...(webrtc.errors ?? []),
          ...(chat.errors ?? [])
        ],
        termsAccepted
      })
    }
  }, [enabled, loaded, webrtc.active, webrtc.realtimeSession, webrtc.errors, termsAccepted, chat.errors]);
  
  const save = useCallback(
    async () => { 
      if (id) { 
        return await updateConversation({
          id,
          update: {
            metadata: chat.conversationMetadata,
            items: chat.messages
          }
        });
      }
    },
    [id, chat.conversationMetadata, chat.messages]
  );
  
  const saveDebounced = useDebouncedCallback(async () => {
    await save();
  }, 1000, [chat.conversationMetadata, chat.messages]);

  useDebouncedEffect(() => {
    if (enabled && loaded && termsAccepted) {
      saveDebounced();
    }
  }, {
    timeWindow: 5_000,
    maxWait: 60_000,
    dependencies: [enabled, loaded, termsAccepted, chat.version]
  });

  // when user leaves this browser window/tab, send save the conversation to the db. it's ok if this is called multiple times
  useEffect(() => {

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        save();
      }
    }

    if (enabled && loaded) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

  }, [save, enabled, loaded]);

  return <ChatSessionProvider context={{
    enabled,
    setEnabled,
    createdAt,
    isLoading,
    loaded,
    termsAccepted,
    setTermsAccepted
  }}>
    {children}
  </ChatSessionProvider>
}
